import axiosInstance from "./axiosConfig";
import type { CaseStrategy } from "./types/classicDrafting";
import { handleAPIError } from "./utils/errors";

const base = (individualId: string) =>
  `/legacy-drafting/${individualId}/case-strategy`;

/** The Case Strategy screen Classic drafting reads before it writes. */
export const CaseStrategyAPI = {
  /** With `draftType`, `criteria` is null when that draft doesn't argue them. */
  async get(individualId: string, draftType?: string): Promise<CaseStrategy> {
    try {
      const response = await axiosInstance.get<CaseStrategy>(
        base(individualId),
        { params: draftType ? { draft_type: draftType } : undefined }
      );
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Fills blank answers from the case files. With `nodeIds` only those are
   * read, and answered questions read differently get a suggestion.
   */
  async autofill(
    individualId: string,
    nodeIds?: string[]
  ): Promise<{ status: string; run_id?: string }> {
    try {
      const response = await axiosInstance.post(
        `${base(individualId)}/autofill`,
        nodeIds?.length ? { node_ids: nodeIds } : {}
      );
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /** Fills one question from the files the attorney picked (up to 20). */
  async fillField(
    individualId: string,
    variableName: string,
    nodeIds: string[]
  ): Promise<{ status: string; run_id: string }> {
    try {
      const response = await axiosInstance.post(
        `${base(individualId)}/fields/${encodeURIComponent(
          variableName
        )}/autofill`,
        { node_ids: nodeIds }
      );
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  async resolveSuggestion(
    individualId: string,
    variableName: string,
    action: "accept" | "discard"
  ): Promise<void> {
    try {
      await axiosInstance.post(
        `${base(individualId)}/fields/${encodeURIComponent(
          variableName
        )}/suggestion`,
        { action }
      );
    } catch (error) {
      throw handleAPIError(error);
    }
  }
};
